import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState, useMemo } from 'react';
import useStyles from './style';
import { HighlightOff } from '@mui/icons-material';
import { useDebounce } from '../../../hooks';
import { Box, Paper, Typography, Stack, Alert } from '@mui/material';
import { getDateFormat } from '../../../utils/common-methods';
import {
  Button,
  GridContainer,
  GridItem,
  Select,
  Breadcrumbs,
  TextField,
  Datepicker,
  Autocomplete,
  FieldWarning,
} from '../../../components/shared';
import { machineUpdateSchema } from '../../../schema/validationSchemas';
import { useParams, useNavigate } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import { useGetModelsQuery } from '../../../services/Models/ModelsService';
import { useGetAllSublocationsQuery } from '../../../services/Sublocation/SublocationService';
import {
  useGetMachineDetailsByIdQuery,
  useGetMachineStatusQuery,
  useUpdateMachineByIdMutation,
} from '../../../services/Machines/MachinesService';
import { dateFormatForApi, getOptions } from '../../../utils/common-methods';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import TimerModal from '../../../components/TimerModal/TimerModal';
import { MESSAGE } from '../../../constants/message';
import { PerformantDecimalField } from '../../../components/shared/PerformantDecimalField/PerformantDecimalField';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ConfirmationModal from '../../../components/ConfirmationModel';
import { parseConflictMessage } from '../../ManageIndepenantRep/parseConflictMessage';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const EditMachine = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    MACHINE: { MACHINE, MACHINE_DETAIL, EDIT_MACHINE },
  } = BREADCRUMB_NAMES;

  const [modelInputValue, setModelInputValue] = useState('');
  const [sublocationInputValue, setSublocationInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [subLocationOptions, setSubLocationOptions] = useState([]);
  const [currentInputVal, setCurrentInputVal] = useState('');
  const [showNoHistoryModal, setShowNoHistoryModal] = useState(false);
  const [serialNumberError, setSerialNumberError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const location = useLocation();
  const version = location.state?.version;
  const disableInEffectFrom = location.state?.disableInEffectFrom;
  const createMachineHistory = location.state?.createMachineHistory;
  const maxInEffectFrom = location.state?.maxInEffectFrom;

  let machineDetailsUrl = `${id}`;
  if (version) {
    machineDetailsUrl += `?version=${version}`;
  }

  const { isSuccess, currentData: currentMachineDetails } = useGetMachineDetailsByIdQuery(machineDetailsUrl);
  const [updateMachineApi] = useUpdateMachineByIdMutation();

  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  // Check if resync is in progress for this machine
  const isResyncInProgress = useMemo(() => {
    if (!isResyncEnabled) return false;
    const resync = currentMachineDetails?.data?.resync;
    if (!resync) return false;
    const items = Array.isArray(resync) ? resync : Object.values(resync).flat();
    return items.some((item) => item && item.status !== 'completed');
  }, [isResyncEnabled, currentMachineDetails?.data?.resync]);

  let MachineTitle = currentMachineDetails?.data?.title || 'N/A';
  const machineStatus = currentMachineDetails?.data?.assetStatus;
  const breadcrumbData = [
    { text: MACHINE, redirection: `${routeConstants.MACHINES_ROUTE}?selectedTab=${machineStatus ? '1' : '2'}` },
    {
      text: MACHINE_DETAIL,
      redirection: `${routeConstants.MACHINES_ROUTE}/${id}`,
    },
    { text: EDIT_MACHINE },
  ];
  useEffect(() => {
    if (isSuccess && currentMachineDetails.data) {
      setValues({
        serialNumber: currentMachineDetails.data.serialNumber || '',
        title: currentMachineDetails.data.title || '',
        statusId: currentMachineDetails.data.statusId || '',
        subLocationId: currentMachineDetails.data.sublocation
          ? {
              text: currentMachineDetails.data.sublocation?.name ?? '',
              value: currentMachineDetails.data.sublocation?.id ?? '',
            }
          : null,
        modelId: currentMachineDetails.data.model
          ? {
              text: currentMachineDetails.data.model?.title,
              value: currentMachineDetails.data.model?.id,
            }
          : null,
        price: currentMachineDetails.data?.price || 0,
        installedDate: currentMachineDetails.data.installedDate,
        uninstalledDate: currentMachineDetails.data.uninstalledDate,
        numberOfPlays: currentMachineDetails.data.numberOfPlays || 0,
        id: currentMachineDetails.data.id || 0,
        legacyAssetId: currentMachineDetails.data.legacyAssetId || '',
        costPerPlay: currentMachineDetails.data.costPerPlay,
        clicksPerPlay: currentMachineDetails.data.clicksPerPlay || 0,
        inEffectFrom: getDateFormat(currentMachineDetails.data.inEffectFrom),
        inEffectTo: getDateFormat(currentMachineDetails.data.inEffectTo),
      });
    }
  }, [isSuccess, currentMachineDetails]);

  const formik = useFormik({
    initialValues: {
      serialNumber: { text: '', value: null },
      title: '',
      statusId: '',
      subLocationId: { text: '', value: null },
      modelId: { text: '', value: '' },
      price: 0,
      installedDate: '',
      uninstalledDate: '',
      numberOfPlays: 0,
      costPerPlay: 0,
      clicksPerPlay: 0,
      id: 0,
      legacyAssetId: '',
      inEffectFrom: '',
      inEffectTo: '',
    },
    validationSchema: machineUpdateSchema,
    onSubmit: async (data) => {
      const machineId = Number(id);
      const apiData = {
        id: machineId,
        serialNumber: data.serialNumber,
        title: data.title,
        statusId: data.statusId,
        modelId: data.modelId.value,
        subLocationId: data?.subLocationId?.value || null,
        installedDate: dateFormatForApi(data.installedDate),
        uninstalledDate: dateFormatForApi(data.uninstalledDate),
        price: data.price || 0,
        clicksPerPlay: data.clicksPerPlay || 0,
        costPerPlay: data.costPerPlay || 0,
        legacyAssetId: data.legacyAssetId || '',
        inEffectFrom: dateFormatForApi(data.inEffectFrom) || null,
        inEffectTo: dateFormatForApi(data.inEffectTo) || null,
        updatedBy: updatedBy,
        ...(version && { version }),
      };

      try {
        const response = await updateMachineApi(apiData);

        if (response.error && response.error.status === 409) {
          if (
            response.error.data.name === 'DateOverlapError' ||
            response.error.data.name === 'ExactMatchReplaceError'
          ) {
            setErrorMessage(response.error.data?.message || 'Date overlap detected. Do you want to override?');
            setShowErrorModal(true);
          } else {
            setSerialNumberError(response.error.data?.message || 'Machine with this serial number already exists.');
          }
        } else {
          onSaveHandler();
        }
      } catch (error) {
        console.error('Error updating machine:', error);
        setErrorMessage('An error occurred while updating the machine. Do you want to retry?');
        setShowErrorModal(true);
      }
    },
  });

  const { currentData: modelResponse } = useGetModelsQuery({
    searchParams: `q=${useDebounce(modelInputValue, 600)}`,
    perPage: 10,
    page: 1,
  });

  const { currentData: sublocationsResponse, isSuccess: subLocationIsSuccess } = useGetAllSublocationsQuery({
    searchParams: `q=${useDebounce(sublocationInputValue, 600)}`,
    perPage: 100,
    page: page,
  });

  useEffect(() => {
    const sublocationTypes = getOptions(sublocationsResponse, null, true);
    setSubLocationOptions((prev) => [...prev, ...sublocationTypes]);
  }, [sublocationsResponse, page]);

  const { currentData: assetStatusTypeResponse } = useGetMachineStatusQuery();

  const assetStatus = useMemo(
    () => getOptions(assetStatusTypeResponse?.data?.assetStatuses),
    [assetStatusTypeResponse],
  );

  const modelTypes = useMemo(() => getOptions(modelResponse?.data?.models), [modelResponse]);

  const onCancelHandler = () => {
    navigate(`/${routeConstants.MACHINES_ROUTE}/${id}`, { redirect: true });
  };

  const onSaveHandler = () => {
    navigate(`/${routeConstants.MACHINES_ROUTE}/${id}`, { redirect: true });
  };

  const handleSaveWithOverride = async () => {
    const machineId = Number(id);
    const apiData = {
      id: machineId,
      serialNumber: values.serialNumber,
      title: values.title,
      statusId: values.statusId,
      modelId: values.modelId.value,
      subLocationId: values?.subLocationId?.value || null,
      installedDate: dateFormatForApi(values.installedDate),
      uninstalledDate: dateFormatForApi(values.uninstalledDate),
      price: values.price || 0,
      clicksPerPlay: values.clicksPerPlay || 0,
      costPerPlay: values.costPerPlay || 0,
      legacyAssetId: values.legacyAssetId || '',
      inEffectFrom: dateFormatForApi(values.inEffectFrom) || null,
      inEffectTo: dateFormatForApi(values.inEffectTo) || null,
      updatedBy: updatedBy,
      override: true, // Add override flag
      ...(version && { version }),
    };

    try {
      const response = await updateMachineApi(apiData);
      if (response.error) {
        setErrorMessage('Failed to update machine even with override.');
        setShowErrorModal(true);
      } else {
        onSaveHandler();
      }
    } catch (error) {
      console.error('Error updating machine with override:', error);
      setErrorMessage('Failed to update machine. Please try again.');
      setShowErrorModal(true);
    }
  };

  const { values, errors, touched, getFieldProps, setFieldValue, setValues } = formik;

  return (
    <Box>
      <Box className={classes.breadcrumbContainer}>
        <Breadcrumbs icon={'/'} variant="smallText" underline="hover" data={breadcrumbData} />
      </Box>
      <Box>
        <Stack direction={'row'} spacing={2} alignItems="center">
          <Typography variant="h1" pb={4} data-testid="machineTitle">
            {MachineTitle}
          </Typography>
        </Stack>
      </Box>
      <Box component={Paper} className={classes.formContent}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0px 8px 16px',
          }}
        >
          <div>
            <Typography variant="h2">Machine Details</Typography>
          </div>
        </div>
        {isResyncInProgress && (
          <Box px={3} pb={2}>
            <Alert severity="warning">
              A resync is currently in progress for this asset. Please try again after resync is complete.
            </Alert>
          </Box>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <GridContainer spacing={{ xs: 1, sm: 2, md: 3 }} alignItems="center">
              <GridItem xs={12} md={6}>
                <TextField
                  className={classes.formFields}
                  label="Manufacturer’s Serial Number"
                  inputProps={{
                    'data-testid': 'form-elements',
                  }}
                  {...getFieldProps('serialNumber')}
                  error={Boolean(serialNumberError)}
                  helperText={serialNumberError}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Machine Name"
                  InputProps={{
                    'data-testid': 'form-elements',
                  }}
                  {...getFieldProps('title')}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Select
                  inputLabel="Status"
                  data-testid="form-elements"
                  options={assetStatus}
                  {...getFieldProps('statusId')}
                  error={Boolean(touched.statusId && errors.statusId)}
                  helperText={touched.statusId && errors.statusId}
                />
              </GridItem>

              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Installed Date"
                  value={values.installedDate ? dayjs.utc(values.installedDate) : null}
                  data-testid="form-elements"
                  inputFormat="MM/DD/YYYY"
                  onChange={(newValue) => setFieldValue('installedDate', newValue)}
                  onBlur={() => formik.setFieldTouched('installedDate')}
                  error={Boolean(touched.installedDate && errors.installedDate)}
                  helperText={touched.installedDate && errors.installedDate}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Uninstall Date"
                  value={values.uninstalledDate ? dayjs.utc(values.uninstalledDate) : null}
                  data-testid="form-elements"
                  inputFormat="MM/DD/YYYY"
                  onChange={(newValue) => setFieldValue('uninstalledDate', newValue)}
                  onBlur={() => formik.setFieldTouched('uninstalledDate')}
                  error={Boolean(touched.uninstalledDate && errors.uninstalledDate)}
                  helperText={touched.uninstalledDate && errors.uninstalledDate}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="No.of Plays"
                  disabled
                  {...getFieldProps('numberOfPlays')}
                  InputProps={{
                    'data-testid': 'form-elements',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  label="Model Name"
                  data-testid="form-elements"
                  value={values.modelId}
                  options={modelTypes || []}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={(e, value) => {
                    setFieldValue('modelId', value);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setModelInputValue(newInputValue);
                  }}
                  onBlur={() => formik.setFieldTouched('modelId')}
                  error={Boolean(touched.modelId && errors.modelId)}
                  helperText={touched.modelId && errors.modelId}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="In Effect From"
                  className={classes.formFields}
                  value={values.inEffectFrom ? dayjs.utc(values.inEffectFrom) : null}
                  InputProps={{
                    'data-testid': 'add-machine-form',
                    sx: {
                      cursor: disableInEffectFrom ? 'not-allowed' : 'pointer',
                      opacity: disableInEffectFrom ? 0.5 : 1,
                      pointerEvents: disableInEffectFrom ? 'none' : 'auto',
                    },
                  }}
                  inputFormat="MM/DD/YYYY"
                  maxDate={createMachineHistory && maxInEffectFrom ? new Date(maxInEffectFrom) : new Date()}
                  minDate={!createMachineHistory && values.inEffectFrom ? new Date(values.inEffectFrom) : null}
                  onChange={(newValue) => {
                    setFieldValue('inEffectFrom', newValue);
                  }}
                  onBlur={() => formik.setFieldTouched('inEffectFrom')}
                  error={Boolean(touched.inEffectFrom && errors.inEffectFrom)}
                  helperText={touched.inEffectFrom && errors.inEffectFrom}
                  disabled={disableInEffectFrom ? disableInEffectFrom : false}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="In Effect To"
                  className={classes.formFields}
                  value={values.inEffectTo ? dayjs.utc(values.inEffectTo) : null}
                  InputProps={{
                    'data-testid': 'add-machine-form',
                    sx: {
                      cursor: !createMachineHistory ? 'not-allowed' : 'pointer',
                      opacity: !createMachineHistory ? 0.5 : 1,
                      pointerEvents: !createMachineHistory ? 'none' : 'auto',
                    },
                  }}
                  inputFormat="MM/DD/YYYY"
                  minDate={createMachineHistory && values.inEffectFrom ? new Date(values.inEffectFrom) : null}
                  maxDate={createMachineHistory && maxInEffectFrom ? new Date(maxInEffectFrom) : null}
                  onChange={(newValue) => setFieldValue('inEffectTo', newValue)}
                  onBlur={() => formik.setFieldTouched('inEffectTo')}
                  error={Boolean(touched.inEffectTo && errors.inEffectTo)}
                  helperText={touched.inEffectTo && errors.inEffectTo}
                  disabled={!createMachineHistory}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="Price"
                  {...getFieldProps('price')}
                  error={Boolean(touched.price && errors.price)}
                  helperText={touched.price && errors.price}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Clicks Per Play"
                  type="number"
                  {...getFieldProps('clicksPerPlay')}
                  InputProps={{
                    'data-testid': 'form-elements',
                  }}
                  error={Boolean(touched.clicksPerPlay && errors.clicksPerPlay)}
                  helperText={touched.clicksPerPlay && errors.clicksPerPlay}
                />
                {!values.clicksPerPlay ? <FieldWarning field="Clicks Per Play"></FieldWarning> : null}
              </GridItem>

              <GridItem xs={12} md={6}>
                <PerformantDecimalField
                  useFormik={true}
                  priceIcon={true}
                  label="Cost Per Play"
                  {...getFieldProps('costPerPlay')}
                  error={Boolean(touched.costPerPlay && errors.costPerPlay)}
                  helperText={touched.costPerPlay && errors.costPerPlay}
                />
                {!values.costPerPlay ? <FieldWarning field="Cost Per Play"></FieldWarning> : null}
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  ListboxProps={{
                    onScroll: (event) => {
                      const listboxNode = event.currentTarget;
                      if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                        if (subLocationIsSuccess && currentInputVal === sublocationInputValue) {
                          setPage((prevPage) => prevPage + 1);
                        }
                      }
                    },
                  }}
                  label="Sub Location"
                  data-testid="form-elements"
                  value={values.subLocationId}
                  options={subLocationOptions || []}
                  getOptionLabel={(option) => option.text || ''}
                  getOptionDisabled={(option) => option.disabled === false}
                  onChange={(e, value) => {
                    setFieldValue('subLocationId', value);
                    setSubLocationOptions(() => []);
                    setPage(() => 1);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setCurrentInputVal(newInputValue);
                    setSubLocationOptions(() => []);
                    setPage(() => 1);
                    setSublocationInputValue(newInputValue);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('subLocationId');
                    setSubLocationOptions(() => []);
                    setPage(() => 1);
                  }}
                  error={Boolean(touched.subLocationId && errors.subLocationId)}
                  helperText={touched.subLocationId && errors.subLocationId}
                />
                {!values.subLocationId?.value && ![1, 4, 5].includes(values.statusId) ? (
                  <FieldWarning field="Sub Location"></FieldWarning>
                ) : null}
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Asset ID"
                  disabled
                  {...getFieldProps('id')}
                  InputProps={{
                    'data-testid': 'form-elements',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Legacy Asset ID(eg. static|475839,MDB|293081,ELT|109372)"
                  {...getFieldProps('legacyAssetId')}
                  InputProps={{
                    'data-testid': 'form-elements',
                  }}
                  error={Boolean(touched.legacyAssetId && errors.legacyAssetId)}
                  helperText={touched.legacyAssetId && errors.legacyAssetId}
                />
              </GridItem>
            </GridContainer>
            <GridContainer justifyContent="space-around" pl={4}>
              <GridItem xs={12} md={12}>
                <Stack direction={'row'} className={classes.btn}>
                  <Stack direction={'row'}>
                    <Button type="Submit" variant="contained" disabled={isResyncInProgress}>
                      <Typography variant="button">{createMachineHistory ? 'ADD' : 'UPDATE'}</Typography>
                    </Button>
                    <Button onClick={onCancelHandler}>
                      <Typography variant="button">CANCEL</Typography>
                    </Button>
                  </Stack>
                </Stack>
              </GridItem>
            </GridContainer>
          </form>
        </FormikProvider>
      </Box>
      <TimerModal
        isOpen={showNoHistoryModal}
        timer={1}
        activate={showNoHistoryModal}
        onTimeComplete={() => {
          setShowNoHistoryModal(false);
        }}
        icon={<HighlightOff color="error" sx={{ fontSize: '4rem' }} />}
        title={MESSAGE.NO_HISTORY}
        msg={`${MESSAGE.NO_MACHINE_HISTORY} This message will be closed in`}
      />
      <ConfirmationModal
        isOpen={showErrorModal}
        title="Update Confirmation"
        msg={parseConflictMessage(errorMessage)}
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          setShowErrorModal(false);
          if (val === 'confirm') {
            handleSaveWithOverride();
          }
        }}
      />
    </Box>
  );
};

export default EditMachine;
